import React, { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import Loading from '../../../../Loading/loading';
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { AuthContext } from "../../../../Header/auth - context";
import { useSelector } from "react-redux";
import { uzmiGresku } from "../../../../custom/greske";

const Dokumenti = ({ filesDB, zavedenaDB, idZaposlenog, onObrisano }) => {
  const { t } = useTranslation();
  const toast = useRef(null);
  const auth = useContext(AuthContext);
  const company = useSelector(state => state.company);

  const [documents, setDocuments] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [first, setFirst] = useState(0);
  const [brisem, setBrisem] = useState(null);   // putanja dokumenta koji se briše

  useEffect(() => {
    const docArray = [];

    if (filesDB) {
      Object.entries(filesDB).forEach(([key, value]) => {
        if (value && value !== "") {
          const paths = value.split('|').filter(p => p.trim() !== '');
          paths.forEach((singlePath, index) => {
            docArray.push({
              name: paths.length > 1 ? `${getDocumentName(key)} (${index + 1})` : getDocumentName(key),
              path: getDocumentLocation(singlePath.trim().replace(/\\/g, '/')),
              /* Putanja se pamti NEDIRNUTA (bez zamene obrnutih crta) — brisanje
                 je traži u bazi po tačnom poklapanju, a stariji zapisi su
                 upisani u Windows obliku. */
              putanja: singlePath.trim(),
              izvor: 'prilog',
              vrsta: t('admin.dokPrilog')
            });
          });
        }
      });
    }

    /* Dokumenta zavedena kroz Štampu dokumentacije: naziv nosi i redni broj,
       jer isti zaposleni može imati više dokumenata istog tipa (npr. novi
       ugovor posle raskida). */
    (zavedenaDB || []).forEach(d => {
      if (!d.putanja) return;
      docArray.push({
        name: `${d.TipDokumenta} (${d.redni_broj}) - ${d.zavedeni_broj}`,
        path: getDocumentLocation(String(d.putanja).replace(/\\/g, '/')),
        putanja: d.putanja,
        /* Zavedeno dokument je PRAVI red u evidenciji, pa se briše svojim
           korakom (menadzerStampaDokumentacije), ne ovim za priloge. */
        izvor: 'zavedeno',
        id: d.id,
        vrsta: t('admin.dokZavedeno')
      });
    });

    setDocuments(docArray);
    /* Brisanjem poslednjeg reda na zadnjoj strani ta strana prestaje da postoji,
       a paginator bi ostao na njoj i tabela bi izgledala prazna. Tada — i samo
       tada — vraća se na prvu stranu. */
    setFirst((prev) => (prev >= docArray.length ? 0 : prev));
  }, [filesDB, zavedenaDB, t]);


 const getDocumentLocation = (normalizedPath) => {
  // Remove full C:/Dokumenta/ from path
  const relativePath = normalizedPath.replace(/^C:\/Dokumenta\//, '');
  const encodedPath = encodeURI(relativePath); // Encode spaces, etc.
  return `${process.env.REACT_APP_BACKEND_URL}/files/${encodedPath}`;
};
  const getDocumentName = (field) => {
    switch (field) {
      case 'uploadedFileLK': return `Lična karta`;
      case 'uploadedFileVD': return `Vozačka dozvola`;
      case 'uploadedFileT': return `Tahograf`;
      case 'uploadedFileO': return `Osiguranje`;
      case 'uploadedFileHealth': return `Zdravstvena knjižica`;
      case 'uploadedFileADR': return `ADR sertifikat`;
      case 'uploadedFileDEF': return `Bezbednost na radu`;
      case 'uploadedFilePASS': return `Pasoš`;
      case 'uploadedFileVIZA': return `Viza`;
      case 'uploadedFileCPC': return `CPC`;
      case 'uploadedFileCPCP1': return `CPC P1`;
      case 'uploadedFileCPCP2': return `CPC P2`;
      case 'uploadedFileCPCP3': return `CPC P3`;
      case 'uploadedFileCPCP4': return `CPC P4`;
      case 'uploadedFileCPCP5': return `CPC P5`;
      case 'foreignUploadedFileLK': return `Lična karta (strana)`;
      case 'foreignUploadedFileVD': return `Vozačka dozvola (strana)`;
      case 'foreignUploadedFilePASS': return `Pasoš (strani)`;
      case 'foreignUploadedFileVIZA': return `Viza (strana)`;
      case 'contractFile': return `Ugovor`;
      default: return `Nema dokumenta`;
    }
  };
  const previewBodyTemplate = (rowData) => {
    return (
      <Button
        label={t('admin.pregledaj')}
        icon="pi pi-eye"
        className="p-button-text"
        onClick={() => window.open(`${rowData.path}`, '_blank')}
      />
    );
  };

  /* Brisanje dokumenta: zapis iz evidencije i fajl sa diska.

     Dva ishoda idu kao DVA toasta, jer su to dva posla koja ne moraju da se
     poklope — zapis je obrisan kad server to potvrdi, a fajla na disku može i
     da nema (ručno uklonjen, premešten). To nije greška, ali korisnik mora da
     zna da na disku nije bilo šta da se briše.

     Dve vrste dokumenata idu na dva mesta: prilog je putanja u koloni
     zaposlenog, a zavedeno dokument je red u evidenciji štampe i ima svoj
     korak DELETE (koji briše i generisani i potpisani primerak). */
  const zovi = async (adresa, telo) => {
    const res = await fetch(process.env.REACT_APP_BACKEND_URL + adresa, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify(telo),
    });
    if (!res.ok) throw res;
    return res.json();
  };

  const obrisiRed = (red) => confirmDialog({
    message: t('admin.dokBrisanjePitanje', { naziv: red?.name || '' }),
    header: t('admin.dokBrisanjeNaslov'),
    icon: 'pi pi-exclamation-triangle',
    acceptClassName: 'p-button-danger',
    acceptLabel: t('common.da'),
    rejectLabel: t('common.ne'),
    accept: async () => {
      setBrisem(red.putanja);
      try {
        const d = red.izvor === 'zavedeno'
          ? await zovi('/employees/stampaDokumentacije', { step: 'DELETE', id: red.id })
          /* `step` ide i kad ga kontroler ne čita: moduleGuard na serveru iz njega
             izvodi nivo prava (brisanje), pa zahtev bez njega visi o nazivu rute. */
          : await zovi('/employees/dokumentaObrisi', { step: 'DELETE', id_zaposleni: idZaposlenog, putanja: red.putanja });

        if (!d?.obrisano) {
          toast.current?.show({
            severity: 'warn',
            summary: t('common.upozorenje'),
            detail: d?.message || t('admin.dokBrisanjeGreska'),
            life: 5000,
          });
          return;
        }

        toast.current?.show([
          { severity: 'success', summary: t('common.uspeh'), detail: d.message, life: 4000 },
          d.fajlObrisan
            ? { severity: 'success', summary: t('common.uspeh'), detail: t('admin.dokFajlObrisan'), life: 4000 }
            : d.fajlPostojao
              ? { severity: 'error', summary: t('common.greska'), detail: t('admin.dokFajlNijeObrisan'), life: 6000 }
              : { severity: 'info', summary: t('admin.m_obavestenje_1'), detail: t('admin.dokFajlNemaNaServeru'), life: 5000 },
        ]);
        if (onObrisano) onObrisano();
      } catch (e) {
        const g = await uzmiGresku(e, t('admin.dokBrisanjeGreska'));
        toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: g.message, life: 5000 });
      } finally {
        setBrisem(null);
      }
    },
  });

  const deleteBodyTemplate = (rowData) => (
    <Button
      type="button"
      icon="pi pi-trash"
      severity="danger"
      rounded
      /* Puna boja, ne `outlined`: u svetloj temi je obrubljeno crveno bledo
         (pastelna paleta aplikacije) i dugme deluje onemogućeno — a onemogućeno
         je ovde pravo stanje (red bez putanje). */
      loading={brisem === rowData?.putanja}
      disabled={!rowData?.putanja}
      onClick={() => obrisiRed(rowData)}
      tooltip={t('common.delete')}
      tooltipOptions={{ position: 'top' }}
    />
  );


  return (
    <div id="zaposleni_dokumenti">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="zaposleni_table">
        <DataTable
          className="stavke-tabela"
          header={(
            <div className="export-buttons fakture-stavke-header">
              <span />
              <input type="search" className="p-inputtext p-component"
                     onInput={(e) => setGlobalFilter(e.target.value)}
                     placeholder={t('common.search')} />
            </div>
          )}
          value={documents}
          paginator
          rows={10}
          first={first}
          globalFilter={globalFilter}
          onPage={(e) => setFirst(e.first)}
        >
          <Column field="name" header={t('admin.nazivDokumenta')} sortable></Column>
          <Column field="vrsta" header={t('admin.dokVrsta')} sortable></Column>
          <Column body={previewBodyTemplate} header={t('fakture.pregled')}></Column>
          <Column body={deleteBodyTemplate} header={t('admin.dokRadnje')} align="center" style={{ minWidth: '90px', maxWidth: '110px' }}></Column>
        </DataTable>
      </div>
    </div>
  );
};

export default Dokumenti;
